import React, { useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useCreateCustomer, CustomerFormInput } from "../../api/customers";
import { useCreateContract } from "../../api/contracts";
import { useApplications } from "../../api/applications";
import { useToast } from "../../components/ToastProvider";
import { CustomerFormFields, BLANK_CUSTOMER_FORM } from "./CustomerFormFields";

export function NewCustomerModal({ show, onClose }: { show: boolean; onClose: () => void }) {
  const [form, setForm] = useState<CustomerFormInput>(BLANK_CUSTOMER_FORM);
  const [planId, setPlanId] = useState("");
  const [monthlyValue, setMonthlyValue] = useState("");
  const [billingDay, setBillingDay] = useState(10);
  const [readjustmentIndex, setReadjustmentIndex] = useState<"IPCA" | "IGPM" | "NONE">("NONE");
  const [error, setError] = useState<string | null>(null);

  const { data: appsData } = useApplications({ pageSize: 100 });
  const plans = useMemo(
    () => (appsData?.data ?? []).flatMap((app) => app.plans.map((p) => ({ ...p, appName: app.name }))),
    [appsData]
  );

  const createCustomer = useCreateCustomer();
  const createContract = useCreateContract();
  const toast = useToast();

  function patch(p: Partial<CustomerFormInput>) {
    setForm((prev) => ({ ...prev, ...p }));
  }

  function reset() {
    setForm(BLANK_CUSTOMER_FORM);
    setPlanId("");
    setMonthlyValue("");
    setBillingDay(10);
    setReadjustmentIndex("NONE");
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.razaoSocial.trim()) return setError("Preencha a razão social do cliente.");
    if (!form.cnpjCpf.trim()) return setError("Informe o CNPJ/CPF do cliente.");
    if (!form.email.trim()) return setError("Informe o e-mail de cobrança.");

    try {
      const res = await createCustomer.mutateAsync(form);
      const customerId = res.data.id;

      if (planId) {
        await createContract.mutateAsync({
          customerId,
          planId: Number(planId),
          billingDay,
          monthlyValue: monthlyValue ? Number(monthlyValue.replace(",", ".")) : undefined,
          readjustmentIndex,
        });
      }

      toast("Cliente cadastrado" + (planId ? " e contrato de assinatura criado." : "."));
      reset();
      onClose();
    } catch {
      setError("Não foi possível cadastrar o cliente — confira os dados e tente de novo.");
    }
  }

  return (
    <Modal backdrop="static" keyboard={false} show={show} onHide={onClose} centered className="nt-dialog" size="lg">
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title style={{ fontSize: 20, fontFamily: "var(--font-heading)" }}>Novo cliente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CustomerFormFields value={form} onChange={patch} />

          <div className="row g-3 mt-1">
            <div className="col-12">
              <strong style={{ fontSize: 12.5 }}>Contrato de assinatura (opcional)</strong>
            </div>
            <div className="col-12 col-sm-6">
              <label className="form-label" style={{ fontSize: 12 }}>
                Aplicação / plano contratado
              </label>
              <select className="form-select" value={planId} onChange={(e) => setPlanId(e.target.value)}>
                <option value="">Sem contrato agora</option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.appName} — {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-3">
              <label className="form-label" style={{ fontSize: 12 }}>
                Mensalidade (R$)
              </label>
              <input
                className="form-control"
                value={monthlyValue}
                onChange={(e) => setMonthlyValue(e.target.value)}
                placeholder="usa o valor do plano"
                disabled={!planId}
              />
            </div>
            <div className="col-12 col-sm-3">
              <label className="form-label" style={{ fontSize: 12 }}>
                Dia de cobrança
              </label>
              <input
                type="number"
                min={1}
                max={28}
                className="form-control"
                value={billingDay}
                onChange={(e) => setBillingDay(Number(e.target.value))}
                disabled={!planId}
              />
            </div>
            <div className="col-12 col-sm-4">
              <label className="form-label" style={{ fontSize: 12 }}>
                Índice de reajuste
              </label>
              <select
                className="form-select"
                value={readjustmentIndex}
                onChange={(e) => setReadjustmentIndex(e.target.value as "IPCA" | "IGPM" | "NONE")}
                disabled={!planId}
              >
                <option value="NONE">Nenhum</option>
                <option value="IPCA">IPCA</option>
                <option value="IGPM">IGP-M</option>
              </select>
            </div>
          </div>
          <p className="mt-3 mb-0" style={{ fontSize: 12.5, opacity: 0.85 }}>
            Ao cadastrar com um plano selecionado, o ERP já cria o contrato de assinatura e agenda a
            primeira cobrança.
          </p>
          {error && (
            <div className="text-danger mt-2" style={{ fontSize: 12.5 }}>
              {error}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-danger" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-success" disabled={createCustomer.isPending || createContract.isPending}>
            Cadastrar cliente
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
